/**
 * Dialog de configuração do node Gerar Cobrança
 * Permite configurar cliente, itens, pagamento e tipo de cobrança
 * 
 * RELEASE: 2.1.0 - 2026-09-07 - Resultado migrado para o modal de destino.
 * RELEASE: 2.0.0 - 2026-03-22 - Catálogo visual de produtos (ProductCatalogModal + DealItemsSummary)
 * RELEASE: 1.0.0 - 2026-03-22 - Novo dialog de configuração
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { VariablePickerCombobox } from '../VariablePickerCombobox';
import { VariableDestinationPicker } from '../VariableDestinationPicker';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  DollarSign,
  Users,
  ShoppingCart,
  CreditCard,
  Calendar,
  Plus,
  AlertCircle,
  Save,
  CheckCircle2,
  XCircle,
  Clock,
  FileText,
  Info,
} from 'lucide-react';
import { FeatureHelp } from '@/components/ui/feature-help';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { FlowNode, ChargeGenerateConfig, ChargeFixedItem } from '@/types/flows';
import { createLogger } from '@/lib/logger';
// Catálogo central de variáveis: fonte única dos nomes exibidos ao usuário
import { CHARGE_OUTPUT_VARIABLE_GROUPS, VARIABLE_CATALOG_BY_TOKEN } from '@/lib/flow/variableCatalog';
import { toast } from 'sonner';
import {
  ProductCatalogModal,
  DealItemsSummary,
} from '@/components/crm/deals/modal/tabs/products';

// Logger do módulo
const logger = createLogger('ConfigureChargeGenerate');

/**
 * Interface para produto pendente (estado local antes de salvar)
 * Usado para pré-configurar produtos antes de salvar o node
 */
interface PendingProduct {
  id: string; // UUID local para identificação no DealItemsSummary
  product_id: string;
  product_name: string;
  quantity: number;
  unit_price: number;
  discount_percent: number;
  total: number;
  product_type?: 'product' | 'service';
  sku?: string | null;
}

interface ConfigureChargeGenerateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  node: FlowNode;
  onSave: (nodeId: string, config: Record<string, unknown>) => void;
}

/**
 * Dialog de configuração do node de geração de cobrança
 */
export function ConfigureChargeGenerateDialog({
  open,
  onOpenChange,
  node,
  onSave,
}: ConfigureChargeGenerateDialogProps) {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id;

  // Estado local do formulário
  const existingConfig = node.data.config as ChargeGenerateConfig | undefined;

  const [customerSelection, setCustomerSelection] = useState<'from_contact' | 'from_deal' | 'from_variable'>(
    existingConfig?.customer_selection || 'from_contact'
  );
  const [customerVariable, setCustomerVariable] = useState(existingConfig?.customer_variable || '');
  const [customerVariableId, setCustomerVariableId] = useState(existingConfig?.customer_variable_id || '');

  const [dealSelection, setDealSelection] = useState<'from_trigger' | 'from_variable' | 'none'>(
    existingConfig?.deal_selection || 'from_trigger'
  );
  const [dealVariable, setDealVariable] = useState(existingConfig?.deal_variable || '');
  const [dealVariableId, setDealVariableId] = useState(existingConfig?.deal_variable_id || '');

  const [itemsSource, setItemsSource] = useState<'from_deal' | 'fixed'>(
    existingConfig?.items_source || 'from_deal'
  );

  // Estado local de produtos pendentes (substitui fixedItems)
  const [pendingProducts, setPendingProducts] = useState<PendingProduct[]>([]);
  const [catalogModalOpen, setCatalogModalOpen] = useState(false);

  const [meioPagamentoId, setMeioPagamentoId] = useState(existingConfig?.meio_pagamento_id || '');
  const [meioPagamentoNome, setMeioPagamentoNome] = useState(existingConfig?.meio_pagamento_nome || '');

  const [contaCorrenteId, setContaCorrenteId] = useState(existingConfig?.conta_corrente_id || '');
  const [contaCorrenteNome, setContaCorrenteNome] = useState(existingConfig?.conta_corrente_nome || '');

  const [tipoCobranca, setTipoCobranca] = useState<'unica' | 'parcelada'>(
    existingConfig?.tipo_cobranca || 'unica'
  );
  const [numeroParcelas, setNumeroParcelas] = useState(existingConfig?.numero_parcelas || 1);

  const [dueDateMode, setDueDateMode] = useState<'days_after' | 'variable'>(
    existingConfig?.due_date_mode || 'days_after'
  );
  const [dueDateDays, setDueDateDays] = useState(existingConfig?.due_date_days || 7);
  const [dueDateVariable, setDueDateVariable] = useState(existingConfig?.due_date_variable || '');
  const [dueDateVariableId, setDueDateVariableId] = useState(existingConfig?.due_date_variable_id || '');

  const [descricao, setDescricao] = useState(existingConfig?.descricao || '');
  const [observacoes, setObservacoes] = useState(existingConfig?.observacoes || '');
  const [nickname, setNickname] = useState(existingConfig?.nickname || '');

  // Variável de resultado da execução
  const [resultVariable, setResultVariable] = useState(existingConfig?.result_variable || '');
  const [resultVariableId, setResultVariableId] = useState(existingConfig?.result_variable_id || '');

  // Variável de CPF/CNPJ do cliente (obrigatória)
  const [cpfCnpjVariable, setCpfCnpjVariable] = useState(existingConfig?.cpf_cnpj_variable || '');
  const [cpfCnpjVariableId, setCpfCnpjVariableId] = useState(existingConfig?.cpf_cnpj_variable_id || '');

  // =========================================================================
  // Query: Meios de pagamento do tenant
  // =========================================================================
  const { data: meiosPagamento = [] } = useQuery({
    queryKey: ['financeiro-meios-pagamento', tenantId],
    queryFn: async () => {
      if (!tenantId) return [];
      const { data, error } = await supabase
        .from('financeiro_meios_pagamento')
        .select('id, nome, tipo_pagamento')
        .eq('tenant_id', tenantId)
        .eq('is_active', true)
        .order('nome');
      if (error) { logger.error('Erro ao buscar meios de pagamento', { error }); return []; }
      return data || [];
    },
    enabled: open && !!tenantId,
  });

  // =========================================================================
  // Query: Contas correntes do tenant
  // =========================================================================
  const { data: contasCorrentes = [] } = useQuery({
    queryKey: ['financeiro-contas-correntes', tenantId],
    queryFn: async () => {
      if (!tenantId) return [];
      const { data, error } = await supabase
        .from('financeiro_conta_corrente')
        .select('id, nome, provider_slug')
        .eq('tenant_id', tenantId)
        .eq('is_active', true)
        .order('nome');
      if (error) { logger.error('Erro ao buscar contas correntes', { error }); return []; }
      return data || [];
    },
    enabled: open && !!tenantId,
  });

  // =========================================================================
  // Resetar formulário quando muda o node selecionado
  // =========================================================================
  useEffect(() => {
    if (open) {
      const cfg = node.data.config as ChargeGenerateConfig | undefined;
      setCustomerSelection(cfg?.customer_selection || 'from_contact');
      setCustomerVariable(cfg?.customer_variable || '');
      setCustomerVariableId(cfg?.customer_variable_id || '');
      setDealSelection(cfg?.deal_selection || 'from_trigger');
      setDealVariable(cfg?.deal_variable || '');
      setDealVariableId(cfg?.deal_variable_id || '');
      setItemsSource(cfg?.items_source || 'from_deal');
      setMeioPagamentoId(cfg?.meio_pagamento_id || '');
      setMeioPagamentoNome(cfg?.meio_pagamento_nome || '');
      setContaCorrenteId(cfg?.conta_corrente_id || '');
      setContaCorrenteNome(cfg?.conta_corrente_nome || '');
      setTipoCobranca(cfg?.tipo_cobranca || 'unica');
      setNumeroParcelas(cfg?.numero_parcelas || 1);
      setDueDateMode(cfg?.due_date_mode || 'days_after');
      setDueDateDays(cfg?.due_date_days || 7);
      setDueDateVariable(cfg?.due_date_variable || '');
      setDueDateVariableId(cfg?.due_date_variable_id || '');
      setDescricao(cfg?.descricao || '');
      setObservacoes(cfg?.observacoes || '');
      setNickname(cfg?.nickname || '');
      setResultVariable(cfg?.result_variable || '');
      setResultVariableId(cfg?.result_variable_id || '');
      setCpfCnpjVariable(cfg?.cpf_cnpj_variable || '');
      setCpfCnpjVariableId(cfg?.cpf_cnpj_variable_id || '');

      // Converter ChargeFixedItem[] para PendingProduct[]
      const items = cfg?.fixed_items || [];
      setPendingProducts(items.map((item, idx) => ({
        id: `local-${idx}-${item.product_id}`,
        product_id: item.product_id,
        product_name: item.product_name,
        quantity: item.quantidade || 1,
        unit_price: item.valor_unitario || 0,
        discount_percent: 0, // Desconto fixo é convertido para percentual na exibição
        total: ((item.quantidade || 1) * (item.valor_unitario || 0)) - (item.desconto || 0),
        product_type: 'product' as const,
        sku: null,
      })));
    }
  }, [open, node.id]);

  // =========================================================================
  // Handlers de produtos (mesmo padrão do ConfigureCrmCreateDealDialog)
  // =========================================================================

  /** Handler para adicionar produto ao estado local via catálogo */
  const handleLocalAddItem = useCallback(async (productId: string, quantity: number, unitPrice: number) => {
    const exists = pendingProducts.find(p => p.product_id === productId);
    if (exists) return;

    // Busca dados do produto para exibição
    const { data: productData } = await supabase
      .from('crm_products')
      .select('id, name, sku, product_type')
      .eq('id', productId)
      .single();

    const newItem: PendingProduct = {
      id: `local-${Date.now()}-${productId}`,
      product_id: productId,
      product_name: productData?.name || 'Produto',
      quantity,
      unit_price: unitPrice,
      discount_percent: 0,
      total: quantity * unitPrice,
      product_type: productData?.product_type as 'product' | 'service' || 'product',
      sku: productData?.sku || null,
    };

    setPendingProducts(prev => [...prev, newItem]);
  }, [pendingProducts]);

  /** Handler para atualizar quantidade do produto pelo product_id (usado pelo modal) */
  const handleLocalUpdateQuantityByProduct = useCallback(async (productId: string, quantity: number) => {
    setPendingProducts(prev => prev.map(p => {
      if (p.product_id === productId) {
        const subtotal = quantity * p.unit_price;
        const discountAmount = p.discount_percent ? subtotal * (p.discount_percent / 100) : 0;
        return { ...p, quantity, total: subtotal - discountAmount };
      }
      return p;
    }));
  }, []);

  /** Handler para remover produto pelo product_id (usado pelo modal) */
  const handleLocalRemoveByProduct = useCallback(async (productId: string) => {
    setPendingProducts(prev => prev.filter(p => p.product_id !== productId));
  }, []);

  /** Handler para atualizar quantidade pelo id local (usado pelo DealItemsSummary) */
  const handleLocalUpdateQuantity = useCallback((itemId: string, quantity: number) => {
    setPendingProducts(prev => prev.map(p => {
      if (p.id === itemId) {
        const subtotal = quantity * p.unit_price;
        const discountAmount = p.discount_percent ? subtotal * (p.discount_percent / 100) : 0;
        return { ...p, quantity, total: subtotal - discountAmount };
      }
      return p;
    }));
  }, []);

  /** Handler para atualizar desconto pelo id local (usado pelo DealItemsSummary) */
  const handleLocalUpdateDiscount = useCallback((itemId: string, percent: number, fixed: number) => {
    setPendingProducts(prev => prev.map(p => {
      if (p.id === itemId) {
        const subtotal = p.quantity * p.unit_price;
        const percentDiscount = percent ? subtotal * (percent / 100) : 0;
        const fixedDiscount = fixed || 0;
        return { ...p, discount_percent: percent, total: subtotal - percentDiscount - fixedDiscount };
      }
      return p;
    }));
  }, []);

  /** Handler para remover produto pelo id local (usado pelo DealItemsSummary) */
  const handleLocalRemoveItem = useCallback((itemId: string) => {
    setPendingProducts(prev => prev.filter(p => p.id !== itemId));
  }, []);

  /** Mapeamento de itens para o formato esperado pelo ProductCatalogModal */
  const dealItemsForModal = useMemo(() => {
    return pendingProducts.map(p => ({
      product_id: p.product_id,
      quantity: p.quantity,
    }));
  }, [pendingProducts]);

  /** Mapeamento de itens para o formato esperado pelo DealItemsSummary */
  const dealItemsForSummary = useMemo(() => {
    return pendingProducts.map(p => ({
      id: p.id,
      product_id: p.product_id,
      quantity: p.quantity,
      unit_price: p.unit_price,
      discount_percent: p.discount_percent,
      discount_fixed: 0,
      total: p.total,
      product: {
        id: p.product_id,
        name: p.product_name,
        sku: p.sku || null,
        product_type: p.product_type || 'product' as const,
        unit_type: 'un',
      },
    }));
  }, [pendingProducts]);

  // =========================================================================
  // Salvar configuração
  // =========================================================================
  const handleSave = () => {
    // Validações
    if (!contaCorrenteId) {
      toast.error('Selecione uma conta corrente');
      return;
    }
    if (!meioPagamentoId) {
      toast.error('Selecione um meio de pagamento');
      return;
    }
    if (itemsSource === 'fixed' && pendingProducts.length === 0) {
      toast.error('Adicione pelo menos um item à cobrança');
      return;
    }
    if (customerSelection === 'from_variable' && !customerVariable) {
      toast.error('Selecione a variável do cliente');
      return;
    }
    if (!cpfCnpjVariable) {
      toast.error('Selecione a variável com CPF/CNPJ do cliente');
      return;
    }

    // Converter PendingProduct[] para ChargeFixedItem[] ao salvar
    const fixedItemsToSave: ChargeFixedItem[] = pendingProducts.map(p => ({
      product_id: p.product_id,
      product_name: p.product_name,
      descricao: p.product_name,
      quantidade: p.quantity,
      valor_unitario: p.unit_price,
      desconto: p.discount_percent ? (p.quantity * p.unit_price * p.discount_percent / 100) : 0,
    }));

    const config: ChargeGenerateConfig = {
      customer_selection: customerSelection,
      customer_variable: customerSelection === 'from_variable' ? customerVariable : undefined,
      customer_variable_id: customerSelection === 'from_variable' ? customerVariableId : undefined,
      deal_selection: dealSelection,
      deal_variable: dealSelection === 'from_variable' ? dealVariable : undefined,
      deal_variable_id: dealSelection === 'from_variable' ? dealVariableId : undefined,
      items_source: itemsSource,
      fixed_items: itemsSource === 'fixed' ? fixedItemsToSave : undefined,
      meio_pagamento_id: meioPagamentoId,
      meio_pagamento_nome: meioPagamentoNome,
      conta_corrente_id: contaCorrenteId,
      conta_corrente_nome: contaCorrenteNome,
      tipo_cobranca: tipoCobranca,
      numero_parcelas: tipoCobranca === 'parcelada' ? numeroParcelas : 1,
      due_date_mode: dueDateMode,
      due_date_days: dueDateMode === 'days_after' ? dueDateDays : undefined,
      due_date_variable: dueDateMode === 'variable' ? dueDateVariable : undefined,
      due_date_variable_id: dueDateMode === 'variable' ? dueDateVariableId : undefined,
      descricao,
      observacoes,
      nickname,
      result_variable: resultVariable || undefined,
      result_variable_id: resultVariableId || undefined,
      cpf_cnpj_variable: cpfCnpjVariable || undefined,
      cpf_cnpj_variable_id: cpfCnpjVariableId || undefined,
    };

    logger.info('Salvando configuração do node charge_generate', { nodeId: node.id });
    onSave(node.id, config as unknown as Record<string, unknown>);
    onOpenChange(false);
    toast.success('Configuração da cobrança salva!');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <DollarSign className="h-5 w-5 text-emerald-500" />
            Configurar Geração de Cobrança
            <FeatureHelp featureKey="chargeGenerate" size="sm" />
          </DialogTitle>
          <DialogDescription>
            Configure a criação automática de cobrança no fluxo
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[65vh] pr-4">
          <div className="space-y-6 py-4">
            {/* ============================================================ */}
            {/* SEÇÃO 1: Apelido do node */}
            {/* ============================================================ */}
            <div className="space-y-2">
              <Label htmlFor="nickname">Apelido do node (opcional)</Label>
              <Input
                id="nickname"
                value={nickname}
                onChange={(e) => setNickname(e.target.value)}
                placeholder="Ex: Cobrança do contrato"
              />
            </div>

            <Separator />

            {/* ============================================================ */}
            {/* SEÇÃO 2: Origem do Cliente */}
            {/* ============================================================ */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-emerald-500" />
                <Label className="text-base font-semibold">Origem do Cliente</Label>
              </div>

              <Select value={customerSelection} onValueChange={(v) => setCustomerSelection(v as any)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="from_contact">Do contato da conversa</SelectItem>
                  <SelectItem value="from_deal">Do negócio (deal)</SelectItem>
                  <SelectItem value="from_variable">De uma variável do fluxo</SelectItem>
                </SelectContent>
              </Select>

              {customerSelection === 'from_variable' && tenantId && (
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Variável com customer_id</Label>
                  <VariablePickerCombobox
                    tenantId={tenantId}
                    value={customerVariableId}
                    onChange={(v) => {
                      setCustomerVariable(v?.name || '');
                      setCustomerVariableId(v?.id || '');
                    }}
                    placeholder="Selecione a variável..."
                  />
                </div>
              )}

              {/* CPF/CNPJ do Cliente (obrigatório) */}
              {tenantId && (
                <div className="space-y-1.5 mt-3 p-3 bg-muted/30 rounded-md border border-border/50">
                  <div className="flex items-center gap-2">
                    <FileText className="h-3.5 w-3.5 text-emerald-500" />
                    <Label className="text-sm font-medium">
                      CPF/CNPJ do Cliente <span className="text-destructive">*</span>
                    </Label>
                  </div>
                  <VariablePickerCombobox
                    tenantId={tenantId}
                    value={cpfCnpjVariableId}
                    onChange={(v) => {
                      setCpfCnpjVariable(v?.name || '');
                      setCpfCnpjVariableId(v?.id || '');
                    }}
                    allowCreate
                    allowClear
                    placeholder="Selecione a variável com CPF ou CNPJ..."
                  />
                  <div className="flex items-start gap-1.5 text-[11px] text-muted-foreground">
                    <Info className="h-3 w-3 mt-0.5 shrink-0" />
                    <span>Usado para cadastrar/atualizar o cliente no provider de cobrança. Detecta automaticamente CPF (11 dígitos) ou CNPJ (14 dígitos).</span>
                  </div>
                </div>
              )}
            </div>

            <Separator />

            {/* ============================================================ */}
            {/* SEÇÃO 3: Origem do Deal */}
            {/* ============================================================ */}
            <div className="space-y-3">
              <Label className="text-base font-semibold">Origem do Negócio (Deal)</Label>

              <Select value={dealSelection} onValueChange={(v) => setDealSelection(v as any)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="from_trigger">Do gatilho (contexto CRM)</SelectItem>
                  <SelectItem value="from_variable">De uma variável do fluxo</SelectItem>
                  <SelectItem value="none">Sem negócio</SelectItem>
                </SelectContent>
              </Select>

              {dealSelection === 'from_variable' && tenantId && (
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Variável com deal_id</Label>
                  <VariablePickerCombobox
                    tenantId={tenantId}
                    value={dealVariableId}
                    onChange={(v) => {
                      setDealVariable(v?.name || '');
                      setDealVariableId(v?.id || '');
                    }}
                    placeholder="Selecione a variável..."
                  />
                </div>
              )}
            </div>

            <Separator />

            {/* ============================================================ */}
            {/* SEÇÃO 4: Origem dos Itens */}
            {/* ============================================================ */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <ShoppingCart className="h-4 w-4 text-emerald-500" />
                <Label className="text-base font-semibold">Itens da Cobrança</Label>
              </div>

              <Select value={itemsSource} onValueChange={(v) => setItemsSource(v as any)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="from_deal">Itens do negócio (deal)</SelectItem>
                  <SelectItem value="fixed">Produtos fixos (catálogo)</SelectItem>
                </SelectContent>
              </Select>

              {itemsSource === 'from_deal' && dealSelection === 'none' && (
                <div className="flex items-center gap-2 text-amber-600 text-xs">
                  <AlertCircle className="h-3.5 w-3.5" />
                  <span>Itens do deal requerem um negócio vinculado</span>
                </div>
              )}

              {/* Itens fixos via catálogo visual */}
              {itemsSource === 'fixed' && (
                <div className="space-y-3">
                  {/* Botão para abrir catálogo */}
                  <Button
                    variant="outline"
                    className="w-full h-10 border-dashed border-2 hover:border-primary/50 hover:bg-primary/5 transition-colors"
                    onClick={() => setCatalogModalOpen(true)}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Adicionar Produto ou Serviço
                  </Button>

                  {/* Resumo dos itens selecionados */}
                  {pendingProducts.length > 0 && (
                    <DealItemsSummary
                      items={dealItemsForSummary}
                      currency="BRL"
                      readOnly={false}
                      onUpdateQuantity={handleLocalUpdateQuantity}
                      onUpdateDiscount={handleLocalUpdateDiscount}
                      onRemove={handleLocalRemoveItem}
                      onOpenCatalog={() => setCatalogModalOpen(true)}
                    />
                  )}

                  {/* Modal de Catálogo de Produtos */}
                  <ProductCatalogModal
                    open={catalogModalOpen}
                    onOpenChange={setCatalogModalOpen}
                    dealId="pending"
                    currency="BRL"
                    workspaceId={null}
                    dealItems={dealItemsForModal}
                    onAddItem={handleLocalAddItem}
                    onUpdateItemQuantity={handleLocalUpdateQuantityByProduct}
                    onRemoveItem={handleLocalRemoveByProduct}
                  />
                </div>
              )}
            </div>

            <Separator />

            {/* ============================================================ */}
            {/* SEÇÃO 5: Pagamento */}
            {/* ============================================================ */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <CreditCard className="h-4 w-4 text-emerald-500" />
                <Label className="text-base font-semibold">Pagamento</Label>
              </div>

              {/* Conta corrente */}
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Conta Corrente</Label>
                <Select
                  value={contaCorrenteId}
                  onValueChange={(v) => {
                    setContaCorrenteId(v);
                    const conta = contasCorrentes.find((c: any) => c.id === v);
                    setContaCorrenteNome(conta ? `${(conta as any).nome} (${(conta as any).provider_slug})` : '');
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione a conta corrente..." />
                  </SelectTrigger>
                  <SelectContent>
                    {contasCorrentes.map((conta: any) => (
                      <SelectItem key={conta.id} value={conta.id}>
                        {conta.nome} ({conta.provider_slug})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Meio de pagamento */}
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Meio de Pagamento</Label>
                <Select
                  value={meioPagamentoId}
                  onValueChange={(v) => {
                    setMeioPagamentoId(v);
                    const meio = meiosPagamento.find((m: any) => m.id === v);
                    setMeioPagamentoNome(meio ? (meio as any).nome : '');
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o meio de pagamento..." />
                  </SelectTrigger>
                  <SelectContent>
                    {meiosPagamento.map((meio: any) => (
                      <SelectItem key={meio.id} value={meio.id}>
                        {meio.nome} ({meio.tipo_pagamento})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Tipo de cobrança */}
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Tipo de Cobrança</Label>
                <Select value={tipoCobranca} onValueChange={(v) => setTipoCobranca(v as any)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unica">Única</SelectItem>
                    <SelectItem value="parcelada">Parcelada</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Parcelas */}
              {tipoCobranca === 'parcelada' && (
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Número de Parcelas</Label>
                  <Select
                    value={String(numeroParcelas)}
                    onValueChange={(v) => setNumeroParcelas(Number(v))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 12 }, (_, i) => i + 1).map(n => (
                        <SelectItem key={n} value={String(n)}>{n}x</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            <Separator />

            {/* ============================================================ */}
            {/* SEÇÃO 6: Vencimento */}
            {/* ============================================================ */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-emerald-500" />
                <Label className="text-base font-semibold">Data de Vencimento</Label>
              </div>

              <Select value={dueDateMode} onValueChange={(v) => setDueDateMode(v as any)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="days_after">Dias após execução</SelectItem>
                  <SelectItem value="variable">Variável do fluxo</SelectItem>
                </SelectContent>
              </Select>

              {dueDateMode === 'days_after' && (
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Dias após execução</Label>
                  <Input
                    type="number"
                    min={1}
                    max={365}
                    value={dueDateDays}
                    onChange={(e) => setDueDateDays(Number(e.target.value))}
                  />
                </div>
              )}

              {dueDateMode === 'variable' && tenantId && (
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Variável com data (YYYY-MM-DD)</Label>
                  <VariablePickerCombobox
                    tenantId={tenantId}
                    value={dueDateVariableId}
                    onChange={(v) => {
                      setDueDateVariable(v?.name || '');
                      setDueDateVariableId(v?.id || '');
                    }}
                    placeholder="Selecione a variável..."
                  />
                </div>
              )}
            </div>

            <Separator />

            {/* ============================================================ */}
            {/* SEÇÃO 7: Descrição e observações */}
            {/* ============================================================ */}
            <div className="space-y-3">
              <div className="space-y-1">
                <Label>Descrição da cobrança</Label>
                <Input
                  value={descricao}
                  onChange={(e) => setDescricao(e.target.value)}
                  placeholder="Ex: Cobrança referente ao contrato #123"
                />
              </div>
              <div className="space-y-1">
                <Label>Observações internas (opcional)</Label>
                <Textarea
                  value={observacoes}
                  onChange={(e) => setObservacoes(e.target.value)}
                  placeholder="Notas internas sobre esta cobrança..."
                  rows={2}
                />
              </div>
            </div>

            <Separator />

            {/* ============================================================ */}
            {/* SEÇÃO 8: Salvar resultado em variável */}
            {/* ============================================================ */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Save className="h-4 w-4 text-emerald-500" />
                <Label className="font-semibold">Salvar resultado em variável (opcional)</Label>
              </div>
              <p className="text-xs text-muted-foreground">
                Salva o status da execução numa variável global para uso em condições posteriores no fluxo.
              </p>

              <VariableDestinationPicker
                tenantId={tenantId || ''}
                value={resultVariableId}
                valueName={resultVariable}
                onChange={(variable) => {
                  setResultVariableId(variable?.id || '');
                  setResultVariable(variable?.name || '');
                }}
                allowClear
                placeholder="Selecione ou crie uma variável..."
              />

              {/* Tabela de valores possíveis */}
              <div className="bg-muted/40 rounded-md p-2.5 space-y-1.5">
                <span className="text-[10px] uppercase font-semibold text-muted-foreground tracking-wider">
                  Valores possíveis:
                </span>
                <div className="space-y-1">
                  <div className="flex items-center gap-2 text-xs">
                    <CheckCircle2 className="h-3 w-3 text-emerald-500" />
                    <code className="bg-emerald-100 dark:bg-emerald-900/30 px-1.5 rounded text-[10px] text-emerald-700 dark:text-emerald-300">created</code>
                    <span className="text-muted-foreground">— Cobrança criada com sucesso no provider</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    <XCircle className="h-3 w-3 text-red-500" />
                    <code className="bg-red-100 dark:bg-red-900/30 px-1.5 rounded text-[10px] text-red-700 dark:text-red-300">error</code>
                    <span className="text-muted-foreground">— Erro durante a criação da cobrança</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    <Clock className="h-3 w-3 text-amber-500" />
                    <code className="bg-amber-100 dark:bg-amber-900/30 px-1.5 rounded text-[10px] text-amber-700 dark:text-amber-300">waiting_provider</code>
                    <span className="text-muted-foreground">— Aguardando confirmação do provider</span>
                  </div>
                </div>
              </div>
            </div>

            <Separator />

            {/* ============================================================ */}
            {/* Variáveis de saída */}
            {/* ============================================================ */}
            {/* Variáveis publicadas na criação da cobrança (financial-charge).
                RELEASE: 2026-09-17 - A lista passa a vir do catálogo central
                (CHARGE_OUTPUT_VARIABLE_GROUPS) para nunca divergir do que o
                sistema realmente publica no contexto do fluxo. */}
            <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-lg p-3 space-y-2">
              <Label className="text-xs font-semibold text-emerald-700 dark:text-emerald-300">
                Variáveis salvas após execução:
              </Label>

              {CHARGE_OUTPUT_VARIABLE_GROUPS.map(grupo => (
                <div key={grupo.label} className="space-y-0.5">
                  <span className="text-[9px] uppercase font-semibold text-muted-foreground tracking-wider">
                    {grupo.label}
                  </span>
                  <div className="flex flex-wrap gap-1">
                    {grupo.tokens.map(v => (
                      <Badge
                        key={v}
                        variant="secondary"
                        className="text-[10px]"
                        title={VARIABLE_CATALOG_BY_TOKEN[v]?.description || ''}
                      >
                        {`{{${v}}}`}
                      </Badge>
                    ))}
                  </div>
                  {grupo.hint && (
                    <p className="text-[10px] text-muted-foreground pt-0.5">{grupo.hint}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </ScrollArea>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleSave} className="bg-emerald-600 hover:bg-emerald-700">
            Salvar Configuração
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}